import React, { ReactElement } from "react"
import {
	Box,
	Button,
	Chip,
	Divider,
	Grid,
	Tooltip,
	Typography,
} from "@mui/material"
import { Trans } from "@lingui/macro"
import {
	AssetMatchBrand,
	AssetMatchesBrandStatus,
} from "models"
import Icon from "@mdi/react"
import {
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
} from "@mdi/js"
import { lightTheme } from "commons/themes"
import withUseRouter, { UseRouterParams } from "services/router"

interface ViewSentProposalsProps extends UseRouterParams {
	visibleMatches: AssetMatchBrand[]
}

class ViewSentProposals extends React.Component<ViewSentProposalsProps, {}> {

	private getCompatibility = (compatibility: number): ReactElement => {

		let emoticon: ReactElement = (
			<Box sx={{ display: "flex", gap: 1 }}>
				<Icon path={mdiEmoticonExcited} size={1} color={lightTheme.palette.info.main} />
				<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
				<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
			</Box>
		)

		if (compatibility <= 66) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 1 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} color={lightTheme.palette.info.main} />
					<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
				</Box>
			)
		}

		if (compatibility <= 33) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 1 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonSad} size={1} color={lightTheme.palette.info.main} />
				</Box>
			)
		}

		return emoticon
	}

	private getStatusChipData = (status: AssetMatchesBrandStatus): ReactElement => {
		switch (status) {
			case AssetMatchesBrandStatus.CREATED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CREATED"}>
								Creato
							</Trans>
						}
						sx={{ backgroundColor: "#adb5bd", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.APPROVED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.APPROVED"}>
								Approvato
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INSPECTION:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INSPECTION"}>
								Sopralluogo
							</Trans>
						}
						sx={{ backgroundColor: "primary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INTERESTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INTERESTED"}>
								Interessato
							</Trans>
						}
						sx={{ backgroundColor: "secondary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.SENT:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.SENT"}>
								Inviato
							</Trans>
						}
						sx={{ backgroundColor: "#ef832e", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.REJECTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.REJECTED"}>
								Rifiutato
							</Trans>
						}
						sx={{ backgroundColor: "#000000", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.REJECTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.REJECTED"}>
								Rifiutato
							</Trans>
						}
						sx={{ backgroundColor: "#000000", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.CONTRACT_DONE:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CONTRACT_DONE"}>
								Contrattualizzata
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
		}
	}

	render() {
		const { visibleMatches } = this.props;

		return (
			<Box
				sx={{
					width: { xs: "100%", lg: "48%" },
					height: { xs: "60vh", lg: "70vh" },
					backgroundColor: "background.paper",
					borderRadius: "10px",
					padding: { xs: 2, sm: 3 },
				}}
			>
				<Typography variant="h6" color="text" sx={{ fontWeight: 600, mb: 2 }}>
					<Trans id="mpc.sent-match.title">Proposte inviate</Trans>
				</Typography>

				<Grid container columnSpacing={2} sx={{ mt: 1, display: { xs: "none", md: "flex" } }}>
					<Grid item md={2.5}>
						<Typography sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							<Trans id="vsp.compatibility">Compatibilità</Trans>
						</Typography>
					</Grid>
					<Grid item md={2.5}>
						<Typography sx={{ fontWeight: 500 }}>
							<Trans id="vsp.brand-name2">Nome Brand</Trans>
						</Typography>
					</Grid>
					<Grid item md={3}>
						<Typography sx={{ fontWeight: 500 }}>
							<Trans id="vsp.operator">Inviata da</Trans>
						</Typography>
					</Grid>
					<Grid item md={2}>
						<Typography sx={{ fontWeight: 500 }} />
					</Grid>
					<Grid item md={1.5}>
						{/* Spazio riservato al pulsante */}
					</Grid>
				</Grid>

				<Box sx={{ pt: 2, pb: 2 }}>
					<Divider sx={{ borderBottomWidth: 2 }} />
				</Box>

				{visibleMatches.length === 0 ? (
					<Box sx={{ mt: 4 }}>
						<Typography variant="body1" color="text.secondary" textAlign={"center"}>
							<Trans id="no-matches-found">Nessuna proposta presente.</Trans>
						</Typography>
					</Box>
				) : (
					<Box sx={{height: { xs: "45vh", lg: "50vh" }, overflowX: "hidden", overflowY: "auto", pr: 1}}>
						{visibleMatches.map((match) => (
							<Grid container key={match.id} sx={{ mb: 2, alignItems: "center" }}>
								<Grid item xs={12} md={2.5} sx={{ mb: { xs: 1, md: 0 } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
											Compatibilità:
										</Typography>
										{this.getCompatibility(match.compatibility || 0)}
									</Box>
								</Grid>
								<Grid item xs={12} md={2.5} sx={{ mb: { xs: 1, md: 0 } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
											Brand:
										</Typography>
										<Tooltip title={match.brand.name}>
											<Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
												{match.brand.name}
											</Typography>
										</Tooltip>
									</Box>
								</Grid>
								<Grid item xs={12} md={3} sx={{ mb: { xs: 1, md: 0 } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
											Inviata da:
										</Typography>
										<Tooltip title={`${match.proposta?.accountManager.accountDetails?.firstName || ""} ${match.proposta?.accountManager.accountDetails?.lastName || ""}`}>
											<Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
												{`${match.proposta?.accountManager.accountDetails?.firstName || ""} ${match.proposta?.accountManager.accountDetails?.lastName || ""}`}
											</Typography>
										</Tooltip>
									</Box>
								</Grid>
								<Grid item xs={12} md={2} sx={{ mb: { xs: 1, md: 0 } }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
											Status:
										</Typography>
										{this.getStatusChipData(match.status)}
									</Box>
								</Grid>
								<Grid item xs={12} md={2}>
									<Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-end" }, mt: { xs: 1, md: 0 } }}>
										<Button
											variant="contained"
											color="info"
											size="small"
											sx={{ px: 2, fontSize: "0.75rem" }}
											onClick={() => this.props.navigate(`/match-proposal/match/${match.id}`)}
										>
											<Trans id="dms-redirect-match-page">Vedi proposta</Trans>
										</Button>
									</Box>
								</Grid>
							</Grid>
						))}
					</Box>
				)}
			</Box>
		);
	}

}

export default withUseRouter(ViewSentProposals)
